import { z } from 'zod';
/**
 * RecruitCRM (RCRM) v1 API — the shapes the vendor sends and accepts (TS-1832).
 *
 * These schemas DESCRIBE a third-party API, they do not police it: every response object is a
 * `z.looseObject` (unknown keys pass through) and every field RCRM is not seen to always send is
 * optional or nullable. A schema that rejects a real response is a bug here, not in RCRM.
 * Evidence for each shape: Relay's RCRM clients and recorded payloads (`Relay/.test/*.json`,
 * `Relay/docs/nitpicks/RCRM/*`) first, the official docs
 * (https://docs.recruitcrm.io/docs/rcrm-api-reference/) where Relay never looked. Where the two
 * disagree, what Relay observed in production wins. The simulator that reproduces them is
 * `apps/rcrm-mock`; its quirks are documented one per file in `docs/nitpicks/recruitcrm/`.
 */
export const RCRM_BASE_URL = 'https://api.recruitcrm.io/v1';
/** Singular record kinds: `entity_type` on custom fields, `related_to_type` on notes. */
export const RCRM_RECORD_TYPES = ['candidate', 'contact', 'company', 'job', 'deal'];
export const rcrmRecordTypeSchema = z.enum(RCRM_RECORD_TYPES);
/** Plural path segments of `GET /v1/custom-fields/{entity}`. */
export const RCRM_CUSTOM_FIELD_ENTITIES = [
    'candidates',
    'contacts',
    'companies',
    'jobs',
    'deals',
];
export const rcrmCustomFieldEntitySchema = z.enum(RCRM_CUSTOM_FIELD_ENTITIES);
/** `deals` → `deal`: the custom-field path segment to the record type its entries carry. */
export const RCRM_ENTITY_RECORD_TYPE = {
    candidates: 'candidate',
    contacts: 'contact',
    companies: 'company',
    jobs: 'job',
    deals: 'deal',
};
/** Timestamps come as `2026-04-30T15:06:03.000000Z` (microseconds, always zero). */
export const rcrmTimestampSchema = z.string();
/** A user as RCRM embeds it (`owner`, `created_by` on an expanded record) and lists it. */
export const rcrmUserSchema = z.looseObject({
    id: z.number(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    email: z.string().nullish(),
    contact_number: z.string().nullish(),
    avatar: z.string().nullish(),
    /** `Active` on every recorded user. */
    status: z.string().nullish(),
    /** Never recorded by Relay; the shape is unknown. */
    role: z.unknown().optional(),
});
/**
 * `owner`, `created_by`, `updated_by`: a bare user id on a record read directly, the whole
 * user object when the record is embedded in another one (`related` on a note).
 */
export const rcrmUserRefSchema = z.union([z.number(), rcrmUserSchema]);
/**
 * One custom field on a record. A record lists EVERY definition of its entity, `value: null`
 * where unset. Values are strings on every recorded payload (a date_time can be unix seconds,
 * `"1778165100"`, or ISO); `user` fields hold user ids.
 */
export const rcrmCustomFieldEntrySchema = z.looseObject({
    field_id: z.number(),
    entity_type: z.string(),
    field_name: z.string(),
    field_type: z.string(),
    value: z.unknown(),
});
/** A definition, from `GET /v1/custom-fields/{entity}`. */
export const rcrmCustomFieldDefinitionSchema = z.looseObject({
    field_id: z.number(),
    entity_type: z.string(),
    field_name: z.string(),
    field_type: z.string(),
    default_value: z.unknown(),
});
/** The definitions endpoint answers a bare array; Relay also accepts `{ data }`. */
export const rcrmCustomFieldDefinitionsResponseSchema = z.union([
    z.array(rcrmCustomFieldDefinitionSchema),
    z.looseObject({ data: z.array(rcrmCustomFieldDefinitionSchema) }),
]);
/**
 * One custom field in a WRITE. Values are always strings; `''` clears the field; RCRM merges
 * (only the entries sent change, Relay `direct-client.ts` "RecruitCRM MERGES a custom_fields
 * POST").
 */
export const rcrmCustomFieldWriteSchema = z.object({
    field_id: z.number().int(),
    value: z.string(),
});
const pageNumber = z.union([z.number(), z.string()]);
/**
 * The list/search envelope (Laravel's paginator). `Relay/.test/recent_jobs.json` has it whole —
 * and `per_page` came back as the STRING `"50"` there, so it is a number or a string. Only
 * `data` is required: every other key is missing from at least one endpoint Relay reads.
 */
export function rcrmListSchema(item) {
    return z.looseObject({
        current_page: pageNumber.optional(),
        data: z.array(item),
        per_page: pageNumber.optional(),
        from: z.number().nullish(),
        to: z.number().nullish(),
        path: z.string().optional(),
        first_page_url: z.string().nullish(),
        next_page_url: z.string().nullish(),
        prev_page_url: z.string().nullish(),
        /** A number, or a numeric string like `per_page`. */
        last_page: pageNumber.optional(),
        total: z.number().optional(),
        last_page_url: z.string().nullish(),
    });
}
/** `GET /v1/users?page=n` — Relay pages it with `{ data, last_page }` and also accepts an array. */
export const rcrmUsersResponseSchema = z.union([
    z.array(rcrmUserSchema),
    rcrmListSchema(rcrmUserSchema),
]);
//# sourceMappingURL=common.js.map